import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useState, useEffect, useRef } from 'react';
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Drawer,
  Stack,
  Alert,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../../config';

interface DirectoryCategory {
  name: string;
  sub_category: string[];
}


interface Directory {
  id: string;
  business_name: string;
  categories: DirectoryCategory[];
  phone_numbers: string[];
  city: string;
  address: string;
  map_url?: string;
  created_at: string;
  updated_at: string;
  rank?: number;
}

interface Category {
  id: string;
  name: string;
  subCategories: string[];
}

const DirectoryManagement: React.FC = () => {
  const [directories, setDirectories] = useState<Directory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingDirectory, setEditingDirectory] = useState<Directory | null>(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formData, setFormData] = useState({
    business_name: '',
    categories: [] as DirectoryCategory[],
    phone_numbers: [''],
    city: '',
    address: '',
    map_url: '',
    rank: 0
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [directoryToDelete, setDirectoryToDelete] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [cities, setCities] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(null);

  // Add this dependency array to prevent unnecessary re-renders
  const debouncedSearchTerm = useRef(searchTerm);
  useEffect(() => {
    debouncedSearchTerm.current = searchTerm;
  }, [searchTerm]);

  useEffect(() => {
    const token = localStorage.getItem('isAuthenticated');
    if (!token) {
      window.location.href = '/admin/login';
      return;
    }
    
    fetchDirectories(true);
    fetchCategories();
    fetchCities();
  }, []);

  const fetchDirectories = async (reset = false) => {
    if (loading || (!hasMore && !reset)) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('isAuthenticated');
      const skip = reset ? 0 : page * 30;
      
      // Build query parameters
      const params: Record<string, string | number> = {
        skip,
        limit: 30
      };

      // Add search parameter if there's a search term
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Add category and city filters
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (selectedCity !== 'all') {
        params.city = selectedCity;
      }
      
      const response = await axios.get(`${API_URL}/admin/directories`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const newDirectories = Array.isArray(response.data) ? response.data : (response.data.directories || []);
      
      if (reset) {
        setDirectories(newDirectories);
        setPage(1);
      } else {
        setDirectories(prev => [...prev, ...newDirectories]);
        setPage(prev => prev + 1);
      }
      
      setHasMore(response.data.hasMore !== undefined ? response.data.hasMore : newDirectories.length === 30);
    } catch (error) {
      console.error('Error fetching directories:', error);
      showSnackbar('Error fetching directories', 'error');
      setHasMore(false);  // Stop trying to fetch more on error
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('isAuthenticated');

      const response = await axios.get(`${API_URL}/admin/categories`,{
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCategories(response.data);
    } catch (error) {
      showSnackbar('Error fetching categories', 'error');
    }
  };

  const fetchCities = async () => {
    try {
      const token = localStorage.getItem('isAuthenticated');
      const response = await axios.get(`${API_URL}/cities`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data && response.data.cities) {
        const sortedCities = [...response.data.cities].sort((a, b) => a.localeCompare(b));
        setCities(sortedCities);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      showSnackbar('Error fetching cities', 'error');
    }
  };

  const handleAddDirectory = async () => {
    if (!formData.business_name.trim() || !formData.categories.length) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('isAuthenticated');
      const response = await axios.post(`${API_URL}/admin/directories`, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setDirectories([...directories, response.data]);
      showSnackbar('Directory added successfully', 'success');
      handleDrawerClose();
    } catch (error) {
      showSnackbar('Error adding directory', 'error');
    }
    setLoading(false);
  };

  const handleUpdateDirectory = async () => {
    if (!editingDirectory || !formData.business_name.trim()) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('isAuthenticated');
      
      const filteredPhoneNumbers = formData.phone_numbers.filter(phone => phone.trim() !== '');
      
      const updateData = {
        business_name: formData.business_name.trim(),
        categories: formData.categories,
        phone_numbers: filteredPhoneNumbers,
        city: formData.city.trim(),
        address: formData.address.trim(),
        map_url: formData.map_url.trim(),
        rank: formData.rank
      };

      console.log('Sending update data:', updateData);

      const response = await axios.put(
        `${API_URL}/admin/directories/${editingDirectory.id}`, 
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data) {
        await fetchDirectories(); // Refresh the entire list
        showSnackbar('Directory updated successfully', 'success');
        handleDrawerClose();
      }
    } catch (error: any) {
      console.error('Update error:', error.response?.data || error);
      showSnackbar(error.response?.data?.message || 'Error updating directory', 'error');
    }
    setLoading(false);
  };

  const handleDeleteClick = (id: string) => {
    console.log('Delete clicked for ID:', id);
    setDirectoryToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!directoryToDelete) return;
    
    try {
      console.log('Deleting directory with ID:', directoryToDelete);
      const token = localStorage.getItem('isAuthenticated');
      const response = await axios.delete(
        `${API_URL}/admin/directories/${directoryToDelete}`, 
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200 || response.status === 204) {
        fetchDirectories();
        showSnackbar('Directory deleted successfully', 'success');
      }
    } catch (error: any) {
      console.error('Delete error:', error.response?.data || error);
      showSnackbar(error.response?.data?.message || 'Error deleting directory', 'error');
    } finally {
      setDeleteConfirmOpen(false);
      setDirectoryToDelete(null);
    }
  };

  const handleEditClick = (directory: Directory) => {
    if (!directory || !directory.id) {
      showSnackbar('Invalid directory selected', 'error');
      return;
    }

    setEditingDirectory(directory);
    setFormData({
      business_name: directory.business_name || '',
      categories: directory.categories || [],
      phone_numbers: directory.phone_numbers?.length ? directory.phone_numbers : [''],
      city: directory.city || '',
      address: directory.address || '',
      map_url: directory.map_url || '',
      rank: directory.rank || 0
    });
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setEditingDirectory(null);
    setFormData({
      business_name: '',
      categories: [],
      phone_numbers: [''],
      city: '',
      address: '',
      map_url: '',
      rank: 0
    });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // Modify the infinite scroll useEffect
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (
          entries[0].isIntersecting && 
          hasMore && 
          !loading
        ) {
          fetchDirectories(false);
        }
      },
      { threshold: 0.1 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => {
      if (loadingRef.current) {
        observer.unobserve(loadingRef.current);
      }
    };
  }, [hasMore, loading, page]);

  // Update the filter handling useEffect
  useEffect(() => {
    setPage(0);
    setDirectories([]); // Clear existing directories
    setHasMore(true); // Reset hasMore to allow new fetches
    fetchDirectories(true);
  }, [searchTerm, selectedCategory, selectedCity]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', py: 4 }}>
      <Helmet>
        <title>Directory Management | ET-Business Directory</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Container maxWidth="lg">
        <Typography variant="h4" component="h1" gutterBottom>
          Directory Management
        </Typography>
        {/* Add your directory management content here */}
      </Container>
    </Box>
  );
};

export default DirectoryManagement; 